import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

import { useCurrentUser, useData } from '../../data/DataContext.tsx';
import type { MyShop, PolicyStatus, PublicProfile, ShopInvite } from '../../data/ports.ts';

// "Acting as": who posts, chats and sells right now — you, or one of your shops.
export type ActingAs = { type: 'user' } | { type: 'shop'; shopId: string };

type Loaded = {
  uid: string;
  status: 'ready' | 'failed' | 'suspended';
  profile: PublicProfile | null;
  suggestion: string | null;
  name: string;
  shops: MyShop[];
  invites: ShopInvite[];
  // null when the Worker could not say; the app does not block on that.
  policies: PolicyStatus | null;
};

export type AccountState = Omit<Loaded, 'uid' | 'status'> & {
  status: 'signed-out' | 'loading' | 'ready' | 'failed' | 'suspended';
  actingAs: ActingAs;
  setActingAs: (value: ActingAs) => void;
  refresh: () => Promise<void>;
};

const EMPTY = { profile: null, suggestion: null, name: '', shops: [], invites: [], policies: null };
const ACTING_KEY = 'guildite.actingAs';

const AccountContext = createContext<AccountState>({
  ...EMPTY, status: 'signed-out', actingAs: { type: 'user' }, setActingAs: () => {}, refresh: async () => {},
});

function storedActingAs(uid: string | null): ActingAs {
  try {
    const value = uid ? JSON.parse(localStorage.getItem(`${ACTING_KEY}.${uid}`) || 'null') : null;
    return value?.type === 'shop' && typeof value.shopId === 'string' ? { type: 'shop', shopId: value.shopId } : { type: 'user' };
  } catch {
    return { type: 'user' };
  }
}

export function AccountProvider({ children }: { children: ReactNode }) {
  const { auth, shops: shopsPort, profiles, policies: policiesPort } = useData();
  const user = useCurrentUser();
  const uid = user?.uid ?? null;
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [acting, setActing] = useState<{ uid: string | null; value: ActingAs }>({ uid, value: storedActingAs(uid) });

  const fetchFor = useCallback(async (forUid: string): Promise<Loaded> => {
    try {
      const [mine, shops, invites, policies] = await Promise.all([
        profiles.getMine(),
        shopsPort.getMyShops(),
        // Invites need a verified email; without one there are simply none to show.
        shopsPort.getMyInvites().catch(() => []),
        policiesPort.status().catch(() => null),
      ]);
      return { uid: forUid, status: 'ready', profile: mine.profile, suggestion: mine.suggestion, name: mine.name, shops, invites, policies };
    } catch (error) {
      // The Worker refuses every request from a suspended account.
      const suspended = (error as { code?: string })?.code === 'account_blocked';
      return { uid: forUid, status: suspended ? 'suspended' : 'failed', ...EMPTY };
    }
  }, [shopsPort, profiles, policiesPort]);

  // Only the newest load is kept: a slow first load must not overwrite a
  // refresh made after, for example, accepting the policies at sign-up.
  const latest = useRef(0);
  const load = useCallback(async (forUid: string) => {
    const request = ++latest.current;
    const result = await fetchFor(forUid);
    if (request === latest.current) setLoaded(result);
  }, [fetchFor]);

  useEffect(() => {
    if (!uid) return;
    void load(uid);
    return () => { latest.current += 1; };
  }, [uid, load]);

  // Reads the signed-in account when called, so a screen that started before
  // sign-in (sign-up) still refreshes the new account.
  const refresh = useCallback(async () => {
    const current = auth.current()?.uid;
    if (current) await load(current);
  }, [auth, load]);

  const setActingAs = useCallback((value: ActingAs) => {
    setActing({ uid, value });
    try {
      if (uid) localStorage.setItem(`${ACTING_KEY}.${uid}`, JSON.stringify(value));
    } catch {
      // Private browsing: the choice lasts for this visit only.
    }
  }, [uid]);

  const current = uid && loaded?.uid === uid ? loaded : null;
  const shops = current?.shops ?? [];
  const chosen = acting.uid === uid ? acting.value : storedActingAs(uid);
  // A shop you left or lost access to falls back to acting as yourself.
  const actingAs: ActingAs = chosen.type === 'shop' && !shops.some(shop => shop.shopId === chosen.shopId) ? { type: 'user' } : chosen;

  const value: AccountState = !uid
    ? { ...EMPTY, status: 'signed-out', actingAs: { type: 'user' }, setActingAs, refresh }
    : current
      ? { profile: current.profile, suggestion: current.suggestion, name: current.name, shops, invites: current.invites, policies: current.policies, status: current.status, actingAs, setActingAs, refresh }
      : { ...EMPTY, status: 'loading', actingAs, setActingAs, refresh };
  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccount(): AccountState {
  return useContext(AccountContext);
}
